// PATH: src/admin/components/CloudinaryMultiImageField.jsx
// VERSION: 2.0 — Multi-upload con drag&drop. Devuelve [{ publicId, title, type }]

import React, { useCallback, useRef, useState } from 'react';
import {
  Box, Button, Flex, Grid, GridItem, Image, Input, Text, useToast, useColorModeValue,
} from '@chakra-ui/react';
import { FiTrash2, FiUploadCloud } from 'react-icons/fi';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';

function slugify(str = '') {
  return str
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().trim().replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'general';
}

export default function CloudinaryMultiImageField({
  label = 'Selecciona imágenes y/o videos',
  value = [],                 // [{ publicId, title, type }]
  onChange,                   // (arr) => void
  folderBase = 'media',       // base de carpeta
  albumTitle = 'album',       // subcarpeta derivada
  previewW = 120,
  previewH = 80,
}) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const defaultBorderColor = useColorModeValue('gray.300', 'gray.600');
  const dragText = useColorModeValue('gray.600', 'gray.400');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'bg.card');
  const thumbBg = useColorModeValue('gray.50', 'gray.700');
  const thumbBorder = useColorModeValue('gray.200', 'gray.600');

  const folder = `${folderBase}/${slugify(albumTitle || 'album')}`;

  const pushItem = useCallback((it) => {
    const arr = Array.isArray(value) ? [...value] : [];
    arr.push(it);
    onChange?.(arr);
  }, [value, onChange]);

  async function handleFiles(files) {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setUploading(true);
    try {
      for (const f of arr) {
        const res = await uploadToCloudinary(f, { folder });
        pushItem({
          publicId: res.publicId,
          title: f.name.replace(/\.[^.]+$/, ''),
          type: res.resourceType === 'video' ? 'video' : 'image',
        });
      }
      toast({ status: 'success', title: `Subidos ${arr.length} archivo(s)` });
    } catch (err) {
      console.error(err);
      toast({ status: 'error', title: err.message || 'Error subiendo archivos' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }

  return (
    <Box>
      <Text mb={1} fontWeight="semibold">{label}</Text>

      <Box
        border="2px dashed"
        borderColor={dragOver ? 'accent.500' : defaultBorderColor}
        borderRadius="md"
        p={4}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        bg={dragOver ? 'blackAlpha.50' : 'transparent'}
      >
        <Flex gap={3} align="center" wrap="wrap">
          <Button
            leftIcon={<FiUploadCloud />}
            onClick={() => inputRef.current?.click()}
            isLoading={uploading}
            loadingText="Subiendo..."
            size="sm"
          >
            Elegir archivos
          </Button>
          <Text fontSize="sm" color={dragText}>
            Arrastra y suelta aquí (imágenes o videos)
          </Text>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </Flex>
      </Box>

      {Array.isArray(value) && value.length > 0 && (
        <Box mt={3}>
          <Grid templateColumns="repeat(auto-fill, minmax(160px, 1fr))" gap={3}>
            {value.map((it, idx) => (
              <GridItem key={`${it.publicId}-${idx}`}>
                <Box
                  border="1px solid"
                  borderColor={cardBorder}
                  borderRadius="md"
                  p={2}
                  bg={cardBg}
                >
                  <Box
                    w={`${previewW}px`}
                    h={`${previewH}px`}
                    bg={thumbBg}
                    border="1px solid"
                    borderColor={thumbBorder}
                    borderRadius="md"
                    overflow="hidden"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mx="auto"
                  >
                    {it.type === 'video' ? (
                      <Box as="video" src={it.preview || ''} controls width={previewW} height={previewH} />
                    ) : (
                      <Image
                        src={it.preview || `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload/w_${previewW},h_${previewH},c_fill,g_auto/${it.publicId}`}
                        alt={it.title || 'item'}
                        width={previewW}
                        height={previewH}
                        objectFit="cover"
                      />
                    )}
                  </Box>

                  <Input
                    mt={2}
                    size="sm"
                    value={it.title || ''}
                    onChange={(e) => {
                      const arr = [...value];
                      arr[idx] = { ...arr[idx], title: e.target.value };
                      onChange?.(arr);
                    }}
                  />

                  <Flex mt={2} justify="space-between" align="center">
                    <Text fontSize="xs" color="gray.500">{it.type}</Text>
                    <Button
                      size="xs"
                      variant="outline"
                      leftIcon={<FiTrash2 />}
                      onClick={() => {
                        const arr = [...value];
                        arr.splice(idx, 1);
                        onChange?.(arr);
                      }}
                    >
                      Quitar
                    </Button>
                  </Flex>
                </Box>
              </GridItem>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
